import { create } from 'zustand';
import api, { setTokens, clearTokens, getAccessToken } from '../lib/api';

// Fase Blindaje — strict 5-minute idle timeout.
// When it fires: call /api/auth/idle-logout (server flips user_status=INACTIVO),
// clear local tokens and redirect to /login with an informative banner.
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

const FORCED_LOGOUT_KEY = 'kompa_forced_logout_reason';

const useAuthStore = create((set, get) => ({
  user: null,
  loading: true,
  _inactivityTimer: null,
  _cleanupListeners: null,
  _shiftEndTimer: null,
  // Surfaced on Login after a forced logout so the user sees WHY.
  // Persisted via sessionStorage because the redirect to /login is a hard
  // reload (window.location.href) which wipes Zustand's in-memory state.
  forcedLogoutReason: (typeof window !== 'undefined' && window.sessionStorage)
    ? (sessionStorage.getItem(FORCED_LOGOUT_KEY) || '')
    : '',

  checkAuth: async () => {
    if (!getAccessToken()) {
      set({ user: false, loading: false });
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data, loading: false });
      get()._startInactivityMonitor();
    } catch (error) {
      console.warn('[auth] session check failed:', error?.response?.status || error?.message);
      clearTokens();
      set({ user: false, loading: false });
    }
  },

  login: async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setTokens(data.token, data.refresh_token);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(FORCED_LOGOUT_KEY);
    }
    set({ user: data.user, forcedLogoutReason: '' });
    get()._startInactivityMonitor();
    return data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.debug('[auth] logout request ignored:', error?.response?.status || error?.message);
    }
    get()._stopInactivityMonitor();
    get()._stopShiftEndMonitor();
    clearTokens();
    set({ user: false });
  },

  // Called by the IdleMonitor after 5 min without activity. Hits the
  // dedicated server endpoint that flips user_status → INACTIVO.
  idleLogout: async (reason = 'inactivity') => {
    try {
      await api.post('/auth/idle-logout', { reason });
    } catch (error) {
      console.debug('[auth] idle-logout request failed (will still clear):', error?.response?.status || error?.message);
    }
    get()._stopInactivityMonitor();
    get()._stopShiftEndMonitor();
    clearTokens();
    const msg =
      reason === 'shift_end'
        ? 'Su jornada laboral finalizó. La sesión fue cerrada y su cuenta pasó a INACTIVA.'
        : reason === 'pause_exceeded'
        ? 'Excedió el tiempo total de Pausa Activa. La sesión fue cerrada y su cuenta pasó a INACTIVA.'
        : reason === 'lunch_overrun'
        ? 'No regresó a tiempo tras el descanso de comida. La sesión fue cerrada y su cuenta pasó a INACTIVA.'
        : 'Sesión cerrada por inactividad (5 minutos). Un superior debe reactivar su cuenta.';
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(FORCED_LOGOUT_KEY, msg);
    }
    set({ user: false, forcedLogoutReason: msg });
  },

  clearForcedLogoutReason: () => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(FORCED_LOGOUT_KEY);
    }
    set({ forcedLogoutReason: '' });
  },

  _startInactivityMonitor: () => {
    const store = get();
    store._stopInactivityMonitor();

    // Política KOMPA: el control de jornada (auto-logout por inactividad,
    // fin de turno, pausas) aplica EXCLUSIVAMENTE a asesores. Para
    // supervisor / gerente / superadmin / formador / qa no se arman timers:
    // su sesión solo termina por logout manual o expiración natural del JWT.
    const role = (get().user?.role || '').toLowerCase();
    if (role !== 'asesor') {
      return;
    }

    const resetTimer = () => {
      const prev = get()._inactivityTimer;
      if (prev) clearTimeout(prev);
      const timer = setTimeout(() => {
        // Fire-and-forget — the idleLogout handles cleanup + redirect
        get().idleLogout('inactivity').finally(() => {
          if (typeof window !== 'undefined') window.location.href = '/login';
        });
      }, INACTIVITY_TIMEOUT_MS);
      set({ _inactivityTimer: timer });
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    set({
      _cleanupListeners: () => {
        events.forEach((e) => window.removeEventListener(e, resetTimer));
      },
    });
  },

  _stopInactivityMonitor: () => {
    const state = get();
    if (state._inactivityTimer) clearTimeout(state._inactivityTimer);
    if (state._cleanupListeners) state._cleanupListeners();
    set({ _inactivityTimer: null, _cleanupListeners: null });
  },

  // Fase 2 — Auto-logout when the user's shift ends.
  // Polls `/api/me/shift-status` once per login (and refreshes hourly), then
  // schedules a single setTimeout that fires at `ends_at`. The handler reuses
  // idleLogout('shift_end') so the server flips status → INACTIVO and the
  // banner explains why.
  _startShiftEndMonitor: async () => {
    get()._stopShiftEndMonitor();
    // Solo asesores tienen jornada laboral acotada (control KOMPA).
    const role = (get().user?.role || '').toLowerCase();
    if (role !== 'asesor') {
      return;
    }
    try {
      const { data } = await api.get('/me/shift-status');
      if (!data?.ends_at) return;
      const msUntil = new Date(data.ends_at).getTime() - Date.now();
      if (msUntil <= 0) return;
      const timer = setTimeout(() => {
        get().idleLogout('shift_end').finally(() => {
          if (typeof window !== 'undefined') window.location.href = '/login';
        });
      }, msUntil);
      set({ _shiftEndTimer: timer });
    } catch (error) {
      console.debug('[auth] shift-status poll failed:', error?.response?.status || error?.message);
    }
  },

  _stopShiftEndMonitor: () => {
    const state = get();
    if (state._shiftEndTimer) clearTimeout(state._shiftEndTimer);
    set({ _shiftEndTimer: null });
  },
}));

export default useAuthStore;
